import {Layout} from '../../src/Layout';
export default Layout;

import docs from 'docs:@react-spectrum/s2';
import {StaticTable} from '../../src/StaticTable';

export const tags = ['navigation', 'collapsible', 'sidebar', 'rail'];
export const relatedPages = [
  {title: 'SideNav', url: './SideNav'}
];
export const version = 'alpha';
export const description = 'A collapsible container for a SideNav and other app chrome.';

# SideNavPanel

<PageDescription>{docs.exports.SideNavPanel.description}</PageDescription>

A `SideNavPanel` wraps a [SideNav](SideNav), along with any other app chrome, in a container that collapses down to a narrow icon rail. It renders its own collapse/expand toggle at the bottom of the panel, and animates its width between the two states.

The expanded width comes from the `styles` prop you pass to `SideNavPanel`.

```tsx render type="s2" files={['packages/dev/s2-docs/pages/s2/SideNavPanelApp.tsx']}
"use client";
import {SideNavPanel, SideNav, SideNavItem, SideNavItemContent, SideNavItemLink, SideNavSection, SideNavHeader, Text} from '@react-spectrum/s2/SideNavPanel';
import {SideNavPanelApp} from './SideNavPanelApp';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import CCLibrary from '@react-spectrum/s2/icons/CCLibrary';
import Files from '@react-spectrum/s2/icons/Files';
import Images from '@react-spectrum/s2/icons/Images';
import Project from '@react-spectrum/s2/icons/Project';

<SideNavPanelApp defaultSelectedRoute="/files">
  {({selectedRoute}) => (
    /*- begin highlight -*/
    <SideNavPanel
      defaultCollapsed
      styles={style({gridArea: 'sidebar', width: 208, marginX: 8})}>
    {/*- end highlight -*/}
      <SideNav
        aria-label="Files"
        selectedRoute={selectedRoute}
        defaultExpandedKeys={['projects']}
        styles={style({width: 'full'})}>
        <SideNavItem href="/files" textValue="Files">
          <SideNavItemContent>
            <SideNavItemLink>
              <Files />
              <Text>Files</Text>
            </SideNavItemLink>
          </SideNavItemContent>
        </SideNavItem>
        <SideNavItem id="your-libraries" href="/your-libraries" textValue="Your Libraries">
          <SideNavItemContent>
            <SideNavItemLink>
              <CCLibrary />
              <Text>Your Libraries</Text>
            </SideNavItemLink>
          </SideNavItemContent>
          <SideNavItem href="/photos" textValue="Photos">
            <SideNavItemContent>
              <SideNavItemLink>
                <Images />
                <Text>Photos</Text>
              </SideNavItemLink>
            </SideNavItemContent>
          </SideNavItem>
        </SideNavItem>
        <SideNavSection>
          <SideNavHeader>Work</SideNavHeader>
          <SideNavItem id="projects" href="/projects" textValue="Projects">
            <SideNavItemContent>
              <SideNavItemLink>
                <Project />
                <Text>Projects</Text>
              </SideNavItemLink>
            </SideNavItemContent>
            <SideNavItem href="/projects/website" textValue="Website redesign">
              <SideNavItemContent>
                <SideNavItemLink>
                  <Text>Website redesign</Text>
                </SideNavItemLink>
              </SideNavItemContent>
            </SideNavItem>
            <SideNavItem href="/projects/mobile" textValue="Mobile app">
              <SideNavItemContent>
                <SideNavItemLink>
                  <Text>Mobile app</Text>
                </SideNavItemLink>
              </SideNavItemContent>
            </SideNavItem>
          </SideNavItem>
        </SideNavSection>
      </SideNav>
    </SideNavPanel>
  )}
</SideNavPanelApp>
```

## Collapsing

The collapsed state is uncontrolled by default. Use `defaultCollapsed` for the initial state, or `isCollapsed` together with `onCollapsedChange` to control it — for example to persist the user's preference, or to collapse the panel from elsewhere in the app.

```tsx render type="s2" files={['packages/dev/s2-docs/pages/s2/SideNavPanelApp.tsx']}
"use client";
import {SideNavPanel, SideNav, SideNavItem, SideNavItemContent, SideNavItemLink, Collection, Text} from '@react-spectrum/s2/SideNavPanel';
import {SideNavPanelApp} from './SideNavPanelApp';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import File from '@react-spectrum/s2/icons/File';
import Files from '@react-spectrum/s2/icons/Files';
import Folder from '@react-spectrum/s2/icons/Folder';
import Images from '@react-spectrum/s2/icons/Images';
import {useState} from 'react';

///- begin collapse -///
interface Item {
  id: number;
  title: string;
  type: 'directory' | 'file';
  href?: string;
  icon?: typeof Folder;
  children?: Item[];
}
let items: Item[] = [
  {id: 1, title: 'Documents', type: 'directory', href: '/documents', icon: Files, children: [
    {id: 2, title: 'Project', type: 'directory', href: '/documents/project', children: [
      {id: 3, title: 'Notes', type: 'file', href: '/notes'},
      {id: 4, title: 'Budget', type: 'file', href: '/budget'}
    ]}
  ]},
  {id: 5, title: 'Photos', type: 'directory', href: '/photos', icon: Images, children: [
    {id: 6, title: 'Image 1', type: 'file', href: '/image-1'},
    {id: 7, title: 'Image 2', type: 'file', href: '/image-2'}
  ]}
];
///- end collapse -///

function CollapsibleSideNavPanel() {
  /*- begin highlight -*/
  let [isCollapsed, setCollapsed] = useState(true);
  /*- end highlight -*/

  return (
    <SideNavPanelApp
      defaultSelectedRoute="/notes"
      /*- begin highlight -*/
      isCollapsed={isCollapsed}
      onCollapsedChange={setCollapsed}>
      {/*- end highlight -*/}
      {({selectedRoute}) => (
        /*- begin highlight -*/
        <SideNavPanel
          isCollapsed={isCollapsed}
          onCollapsedChange={setCollapsed}
          styles={style({gridArea: 'sidebar', width: 208, marginX: 8})}>
        {/*- end highlight -*/}
          <SideNav
            aria-label="Files"
            selectedRoute={selectedRoute}
            items={items}
            styles={style({width: 'full'})}>
            {function renderItem(item) {
              let Icon = item.icon ?? (item.type === 'directory' ? Folder : File);
              return (
                <SideNavItem textValue={item.title} href={item.href}>
                  <SideNavItemContent>
                    <SideNavItemLink>
                      <Icon />
                      <Text>{item.title}</Text>
                    </SideNavItemLink>
                  </SideNavItemContent>
                  {item.children && <Collection items={item.children}>
                    {renderItem}
                  </Collection>}
                </SideNavItem>
              );
            }}
          </SideNav>
        </SideNavPanel>
      )}
    </SideNavPanelApp>
  );
}

<CollapsibleSideNavPanel />
```

## Animations

`SideNavPanel` and [SideNav](SideNav) animate themselves. The panel's width animates first, and its contents animate only once it has settled.

Because the contents wait for the width, the `isCollapsed` you read from `SideNavPanelContext` trails the panel itself while it is expanding.

`AccountFooter` in the example below reads `isCollapsed` from `SideNavPanelContext` and hides its name with `display: none` while the panel is collapsed.

<StaticTable
  headers={['Context value', 'Description']}
  codeColumns={[0]}
  rows={[
    ["isCollapsed", "Whether the panel's contents are collapsed. This trails the panel itself while it is expanding, since its contents are only brought back once it has finished widening."],
    ['setCollapsed', 'Collapses or expands the panel. Useful for building your own expand and collapse trigger.']
  ]} />

```tsx render type="s2" files={['packages/dev/s2-docs/pages/s2/SideNavPanelApp.tsx']}
"use client";
import {SideNavPanel, SideNav, SideNavItem, SideNavItemContent, SideNavItemLink, Collection, Text} from '@react-spectrum/s2/SideNavPanel';
import {SideNavPanelApp, AccountFooter} from './SideNavPanelApp';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import File from '@react-spectrum/s2/icons/File';
import Files from '@react-spectrum/s2/icons/Files';
import Images from '@react-spectrum/s2/icons/Images';
import Archive from '@react-spectrum/s2/icons/Archive';
import Folder from '@react-spectrum/s2/icons/Folder';

///- begin collapse -///
interface Item {
  id: number;
  title: string;
  type: 'directory' | 'file';
  href?: string;
  icon?: typeof Folder;
  children?: Item[];
}
let items: Item[] = [
  {id: 1, title: 'Documents', type: 'directory', href: '/documents', icon: Files, children: [
    {id: 2, title: 'Project', type: 'directory', href: '/documents/project', children: [
      {id: 3, title: 'Notes', type: 'file', href: '/notes'},
      {id: 4, title: 'Budget', type: 'file', href: '/budget'}
    ]}
  ]},
  {id: 5, title: 'Photos', type: 'directory', href: '/photos', icon: Images, children: [
    {id: 6, title: 'Image 1', type: 'file', href: '/image-1'},
    {id: 7, title: 'Image 2', type: 'file', href: '/image-2'}
  ]},
  {id: 8, title: 'Archive', type: 'directory', href: '/archive', icon: Archive, children: [
    {id: 9, title: 'Invoices', type: 'file', href: '/invoices'},
    {id: 10, title: 'Contracts', type: 'file', href: '/contracts'},
    {id: 11, title: 'Receipts', type: 'file', href: '/receipts'}
  ]}
];
///- end collapse -///

<SideNavPanelApp defaultSelectedRoute="/notes">
  {({selectedRoute}) => (
    <SideNavPanel defaultCollapsed styles={style({gridArea: 'sidebar', width: 208, marginX: 8})}>
      <SideNav
        aria-label="Files"
        selectedRoute={selectedRoute}
        items={items}
        styles={style({width: 'full'})}>
        {function renderItem(item) {
          let Icon = item.icon ?? (item.type === 'directory' ? Folder : File);
          return (
            <SideNavItem textValue={item.title} href={item.href}>
              <SideNavItemContent>
                <SideNavItemLink>
                  <Icon />
                  <Text>{item.title}</Text>
                </SideNavItemLink>
              </SideNavItemContent>
              {item.children && <Collection items={item.children}>
                {renderItem}
              </Collection>}
            </SideNavItem>
          );
        }}
      </SideNav>
      {/*- begin highlight -*/}
      <AccountFooter />
      {/*- end highlight -*/}
    </SideNavPanel>
  )}
</SideNavPanelApp>
```

## API

```tsx links={{SideNavPanel: '#sidepanel', SideNav: 'SideNav', SideNavItem: 'SideNav#sidenavitem', SideNavItemContent: 'SideNav#sidenavitemcontent', SideNavItemLink: 'SideNav#sidenavitemlink', Icon: 'icons', Text: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/span'}}
<SideNavPanel>
  <SideNav>
    <SideNavItem href>
      <SideNavItemContent>
        <SideNavItemLink>
          <Icon />
          <Text />
        </SideNavItemLink>
      </SideNavItemContent>
    </SideNavItem>
  </SideNav>
</SideNavPanel>
```

### SideNavPanel Props

<PropTable component={docs.exports.SideNavPanel} links={docs.links} />
